import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Compass } from 'lucide-react'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { CardContent, CardIcon } from '@/components/ui/card'
import { AuthService } from '@/services/core/auth'
import { SitesApi, Site } from '@/services/sites/sitesApi'
import { apiErrorDescription } from '@/services/core/apiError'
import type { ListSort } from '@/services/core/paging'
import { AppShell } from '../../components/AppShell'
import { SiteTable } from '../../components/SiteTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ALL_COUNTRIES, CountryFilter } from '@/components/CountryFilter'
import { FilterField } from '@/components/FilterButton'
import { EarningsAccountSelect } from '@/components/EarningsAccountSelect'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { ImportExportButtons } from '../../components/ImportExportButtons'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useNetwork } from '@/contexts/NetworkContext'
import { useUrlSearch } from '@/hooks/useUrlSearch'

export function SitesPage() {
  const { t } = useTranslation()
  const hasToken = AuthService.isAuthenticated()
  const queryClient = useQueryClient()

  const [search, setSearch] = useUrlSearch()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.sites.status', 'ACTIVE')
  // Marketplace networks sell inventory across countries, so their site list
  // gets a country filter; a single country network has no use for one.
  const { currentNetwork, scopeKey, scopeReady } = useNetwork()
  const showCountryFilter = !!currentNetwork?.marketplacePlatform
  const [countryFilter, setCountryFilter] = usePersistedState<string>('filters.sites.country', ALL_COUNTRIES)
  const country = showCountryFilter ? countryFilter : ALL_COUNTRIES
  const [publisherFilter, setPublisherFilter] = usePersistedState<string>('filters.sites.publisher', '')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const [selection, setSelection] = useState<Site[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  // Every filter is a request parameter, so the screen only ever holds the one
  // page it shows — the whole list is walked for a download and nothing else.
  const listFilters = {
    search,
    status: statusFilter,
    countries: country === ALL_COUNTRIES ? undefined : [country],
    earningsAccountId: publisherFilter || undefined,
  }
  const { data, isLoading: loading } = useQuery({
    queryKey: [
      'sites',
      scopeKey,
      search,
      statusFilter,
      country,
      publisherFilter,
      page,
      pageSize,
      sort,
    ],
    queryFn: () => SitesApi.getPage({ ...listFilters, page, pageSize, sort }),
    enabled: hasToken && scopeReady,
    staleTime: 5 * 60 * 1000,
  })
  const sites = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  // The countries on offer come from the network, not from the loaded rows —
  // those only hold the page being shown.
  const { data: siteCountries = [] } = useQuery({
    queryKey: ['sites', 'countries', scopeKey],
    queryFn: () => SitesApi.getCountries(),
    enabled: hasToken && showCountryFilter,
    staleTime: 5 * 60 * 1000,
  })
  const countryCodes = useMemo(() => siteCountries.map((c) => c.country), [siteCountries])

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((s) => SitesApi.update(s.id, { name: s.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'sites.doneDetail_activate' : 'sites.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  return (
    <AppShell crumbs={[{ label: t('sites.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('sites.title')}</h1>
            <p>{t('sites.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <ImportExportButtons
              loadAll={() => SitesApi.getAll(search, listFilters.countries, statusFilter, listFilters.earningsAccountId)}
              filename="sites"
              idFields={['siteRuleset', 'targetingRulesets', 'sizeRulesets', 'layoutRulesets']}
              excludeFields={['validationWarnings', 'politicalAdvertising', 'ownerTeamId', 'ownerTeamName', 'requestedInLastDay', 'createTime', 'updateTime']}
              templateFields={['id', 'name', 'objectState', 'labels', 'country', 'earningsAccountId', 'earningsAccountName', 'paywalled', 'iabCategories', 'filterTeamsType', 'filterTeams', 'siteRuleset', 'targetingRulesets', 'sizeRulesets', 'layoutRulesets']}
              templateExample={{
                id: '',
                name: 'My Site',
                objectState: 'ACTIVE',
                labels: '',
                country: 'NO',
                earningsAccountId: '',
                earningsAccountName: '',
                paywalled: 'false',
                iabCategories: '',
                filterTeamsType: 'PRIVATE',
                filterTeams: '',
                siteRuleset: '',
                targetingRulesets: '',
                sizeRulesets: '',
                layoutRulesets: '',
              }}
              onUpload={async (rows) => {
                // `earningsAccountName` only labels the account in the file —
                // `earningsAccountId` is the field that is actually saved.
                const stripKeys = new Set(['validationWarnings', 'politicalAdvertising', 'earningsAccountName', 'ownerTeamId', 'ownerTeamName', 'requestedInLastDay', 'createTime', 'updateTime'])
                const items = rows.map((row) => {
                  const { id, ...rest } = row as Record<string, unknown>
                  for (const k of stripKeys) delete rest[k]
                  if (!rest.country) delete rest.country
                  if (!rest.earningsAccountId) delete rest.earningsAccountId
                  return { id: id as string, ...rest }
                })
                await SitesApi.bulkSave(items as (Parameters<typeof SitesApi.update>[1] & { id: string })[])
                queryClient.invalidateQueries({ queryKey: ['sites'] })
              }}
            />
            <CreateObjectButton type="site" size="default" />
          </div>
        </div>

        <ListCard tone="delivery">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Compass} />
                {t('sites.title')}
              </>
            }
            count={totalCount}
            actions={<PageSizeSelect value={pageSize} onChange={(size) => refilter(() => setPageSize(size))} />}
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                  extraActiveCount={(country !== ALL_COUNTRIES ? 1 : 0) + (publisherFilter ? 1 : 0)}
                  onReset={() =>
                    refilter(() => {
                      setStatusFilter('ACTIVE')
                      setCountryFilter(ALL_COUNTRIES)
                      setPublisherFilter('')
                    })
                  }
                >
                  <FilterField label={t('sites.publisher')}>
                    <EarningsAccountSelect
                      value={publisherFilter}
                      onChange={(v) => refilter(() => setPublisherFilter(v))}
                      anyLabel={t('common.all')}
                      className="h-8"
                    />
                  </FilterField>
                  {showCountryFilter && (
                    <FilterField label={t('common.country')}>
                      <CountryFilter
                        value={countryFilter}
                        onChange={(v) => refilter(() => setCountryFilter(v))}
                        codes={countryCodes}
                      />
                    </FilterField>
                  )}
                </StatusFilterButton>
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              { label: t('common.activate'), icon: 'pi pi-check-circle', severity: 'success', onClick: () => bulkSetStatus('ACTIVE') },
              { label: t('common.deactivate'), icon: 'pi pi-ban', severity: 'danger', onClick: () => bulkSetStatus('INACTIVE') },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <SiteTable
              sites={sites}
              loading={loading}
              showEarningsAccount
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={
                hasToken ? t('sites.noSitesFound') : t('common.signInToLoad')
              }
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('sites.siteCount', { count: totalCount })}
          />

        </ListCard>
      </div>
    </AppShell>
  )
}
